import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { JwtHelperService } from '@auth0/angular-jwt';

import { LocalStorage } from '../modules/shared/local-storage';
import { FetchCacheService } from './fetch-cache.service';
import { MatDialog } from '@angular/material/dialog';
import { SignalStoreCache, SignalStoreResetRegistry } from '../modules/shared/signal-store';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private storeResetRegistry = inject(SignalStoreResetRegistry);
  private storeCache = inject(SignalStoreCache);
  private readonly platformId = inject(PLATFORM_ID);

  @LocalStorage private _token: string | null = null;
  @LocalStorage private refreshToken: string | null = null;
  @LocalStorage private expiration: number | null = null;

  private _payload = new BehaviorSubject<{ permissions: string[]; [key: string]: unknown } | null>(null);
  private _isLogged = new BehaviorSubject(this.token != null);

  timeoutId: any;

  constructor(
    private injector: Injector,
    private fcService: FetchCacheService,
    private dialogRef: MatDialog
  ) {
    this.setTimer();

    if (this._token) {
      this._setPayload(this._token);
    }
  }

  get isLogged$() {
    return this._isLogged.asObservable();
  }

  get isLogged() {
    return this._isLogged.value;
  }

  get token() {
    return this._token;
  }

  get payload() {
    return this._payload.value;
  }

  get payload$() {
    return this._payload;
  }

  setToken(token: string | null) {
    this._token = token;
    this._isLogged.next(token != null);
    if (token) {
      this._setPayload(token);
    } else {
      this._payload.next(null);
    }
  }

  private _setPayload(token: string) {
    const helper = new JwtHelperService();
    const payload = helper.decodeToken(token);
    this._payload.next(payload);
  }

  get permissions() {
    return this.payload?.permissions;
  }

  async login(username: string, password: string): Promise<boolean> {
    try {
      // Ignore HTTP Interceptors
      const response = (await this.getCleanClient()
        .post(`${environment.authApi}/oauth/token`, {
          client_id: environment.clientId,
          grant_type: 'password',
          username,
          password
        })
        .toPromise()) as any;

      this.storeResetRegistry.resetAll();
      await this.storeCache.clearAll();
      this.refreshToken = response.refresh_token;
      this.setToken(response.access_token);
      this.expiration = response.created_at + response.expires_in;
      this.setTimer();
    } catch (e) {
      console.error('error during logging: ', e);
      this.clearAll();

      return false;
    }

    return true;
  }

  refresh(): Observable<boolean> {
    const refreshed = new BehaviorSubject<boolean>(false);
    this.getCleanClient()
      .post<any>(`${environment.authApi}/oauth/token`, {
        client_id: environment.clientId,
        grant_type: 'refresh_token',
        refresh_token: this.refreshToken
      })
      .subscribe(
        response => {
          this.refreshToken = response.refresh_token;
          this.setToken(response.access_token);
          this.expiration = response.created_at + response.expires_in;
          refreshed.next(true);
          this.setTimer();
        },
        error => {
          this.clearAll();
          refreshed.next(false);
        }
      );

    return refreshed;
  }

  async logout() {
    try {
      await this.getCleanClient()
        .post(`${environment.authApi}/oauth/revoke`, {
          client_id: environment.clientId,
          auth_token: this.token
        })
        .toPromise();
    } catch (e) {
      console.error(e);
    }

    this.clearAll();
  }

  private setTimer() {
    if (isPlatformServer(this.platformId) || this.expiration == null) {
      return;
    }

    const expiration = this.expiration - new Date().getTime() / 1000 - 10;
    if (expiration <= 0) {
      this.refresh();
      return;
    }

    this.timeoutId = setTimeout(() => {
      this.refresh();
    }, expiration * 1000);
  }

  private clearAll() {
    this.refreshToken = null;
    this.expiration = null;
    this.setToken(null);
    clearTimeout(this.timeoutId);
    this.dialogRef.closeAll();

    if (typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
    this.fcService.dropDatabase();
    this.storeResetRegistry.resetAll();
    this.storeCache.clearAll();
  }

  private getCleanClient() {
    const backend = this.injector.get(HttpBackend);
    return new HttpClient(backend);
  }
}
